import {
  useCallback, useEffect, useRef, useState,
} from 'react'
import { useVideoState } from '../../OracleCommons/useVideoState'
import type { ConceptMoment } from './useConceptMoments'

/**
 * Options for the useConceptTrigger hook
 */
export interface UseConceptTriggerOptions {
  /** Dense moments from the VideoConceptMoments API */
  moments: ConceptMoment[] | null
  /** Whether the AI Guide panel is currently open */
  isGuideOpen: boolean
  /** Whether the toast has been dismissed for the current video (all moments) */
  isDismissed: boolean
}

/**
 * Result from the useConceptTrigger hook
 */
export interface UseConceptTriggerResult {
  /** Whether the toast should be shown */
  shouldShowToast: boolean
  /** The moment that triggered the current toast (for fetching its summary) */
  activeMoment: ConceptMoment | null
  /** Directly hide the toast (bypasses Redux round-trip) */
  dismiss: () => void
}

/**
 * Hook that determines when to show the toast nudge based on video
 * playback position and dense concept moments from the API.
 *
 * Polls the video player at 1-second intervals.
 *
 * Toast visibility rules:
 * - Show when playback is inside a moment window (startSeconds–endSeconds)
 * - Hide when playback leaves the window
 * - If the user seeks back into the window, show again (unless user-dismissed)
 * - User-dismissed moments (X button or action click) are tracked and won't re-show
 * - If user seeks into a different moment's window, that moment shows instead
 * - If the guide was open during a moment (user already saw seed prompts),
 *   the toast is suppressed for that moment when the guide closes
 *
 * activeMoment tracks playback position regardless of guide state,
 * so moment-scoped prompts work whether the guide is open or closed.
 */
export function useConceptTrigger (
  options: UseConceptTriggerOptions,
): UseConceptTriggerResult {
  const {
    moments,
    isGuideOpen,
    isDismissed,
  } = options

  const { contentId, getPlaybackState } = useVideoState()
  const [shouldShowToast, setShouldShowToast] = useState(false)
  const [activeMoment, setActiveMoment] = useState<ConceptMoment | null>(null)

  // Track the contentId to reset on video change
  const prevContentIdRef = useRef<string | null>(null)

  // Track moments the user explicitly dismissed (X button or action click).
  // These won't re-show even if the user seeks back into the window.
  const dismissedMomentsRef = useRef<Set<number>>(new Set())

  // Track moments encountered while the guide was open. The user already
  // engaged via seed prompts, so the toast is redundant for these moments.
  // Cleared when playback leaves the moment window (rewind re-engages).
  const seenWhileGuideOpenRef = useRef<Set<number>>(new Set())

  // Refs to expose latest values inside setInterval closures
  const isGuideOpenRef = useRef(isGuideOpen)
  const isDismissedRef = useRef(isDismissed)

  // Keep refs in sync
  useEffect(() => { isGuideOpenRef.current = isGuideOpen }, [isGuideOpen])
  useEffect(() => { isDismissedRef.current = isDismissed }, [isDismissed])

  // Reset trigger state when contentId changes (new video)
  useEffect(() => {
    if (contentId !== prevContentIdRef.current) {
      prevContentIdRef.current = contentId
      dismissedMomentsRef.current = new Set()
      seenWhileGuideOpenRef.current = new Set()
      setShouldShowToast(false)
      setActiveMoment(null)
    }
  }, [contentId])

  // Poll playback position at 1-second intervals.
  // On each tick, determine which moment (if any) the playback is inside,
  // and show/hide the toast accordingly.
  useEffect(() => {
    if (isDismissed || !moments || moments.length === 0) {
      return undefined
    }

    const interval = setInterval(() => {
      const { currentTimeSeconds } = getPlaybackState()
      if (currentTimeSeconds == null) return

      // Suppress toast while guide is open or globally dismissed,
      // but keep tracking activeMoment so moment prompts work when guide is open
      if (isGuideOpenRef.current || isDismissedRef.current) {
        if (shouldShowToast) {
          setShouldShowToast(false)
        }
      }

      // Find the moment whose window the playback is currently inside
      // (and that the user hasn't explicitly dismissed)
      const currentMoment = moments.find(
        (m) => !dismissedMomentsRef.current.has(m.startSeconds)
          && currentTimeSeconds >= m.startSeconds
          && currentTimeSeconds <= m.endSeconds,
      )

      if (currentMoment) {
        // Mark moments encountered while guide is open — the user already
        // has access to seed prompts so the toast would be redundant
        if (isGuideOpenRef.current) {
          seenWhileGuideOpenRef.current.add(currentMoment.startSeconds)
        }

        if (activeMoment?.startSeconds !== currentMoment.startSeconds) {
          setActiveMoment(currentMoment)
        }
        // Only show toast when guide is closed, not dismissed,
        // and moment wasn't already seen while guide was open
        if (!isGuideOpenRef.current && !isDismissedRef.current
          && !seenWhileGuideOpenRef.current.has(currentMoment.startSeconds)) {
          if (!shouldShowToast) setShouldShowToast(true)
        }
      } else {
        // Playback left the moment window. Keep dismissed state so
        // user-dismissed moments stay dismissed for the video session.
        // Dismissed moments are only cleared on video change (line 86).
        if (activeMoment) {
          setActiveMoment(null)
        }
        if (shouldShowToast) setShouldShowToast(false)
      }
    }, 1000)

    return () => { clearInterval(interval) }
  }, [
    moments,
    isDismissed,
    getPlaybackState,
    shouldShowToast,
    activeMoment,
  ])

  // Dismiss the current moment. Called by the parent on X button or action click.
  // Marks this specific moment as user-dismissed so it won't re-show on seek.
  const dismiss = useCallback(() => {
    if (activeMoment) {
      dismissedMomentsRef.current.add(activeMoment.startSeconds)
    }
    setShouldShowToast(false)
  }, [activeMoment])

  // Suppress toast when guide is open or globally dismissed
  const effectiveShouldShow = shouldShowToast && !isGuideOpen && !isDismissed

  return {
    shouldShowToast: effectiveShouldShow,
    activeMoment,
    dismiss,
  }
}
